import { ITranscriptionSegment } from "services/TranscriptionService";
import { IChatRecord } from "../Chat/AcxChatViewer";
import { useCallback, useEffect, useState } from "react";
import { uniqBy } from "lodash";
import P2BlurbBox from "components/UI/P2Blurbs/P2BlurbBox";
import React from "react";
import { Box, Divider, SxProps, Typography, useTheme } from "@mui/material";
import { convertTranscriptToBlurbs } from "../AcxTranscription";
import { SpeakerChannelMap } from "../AcxTranscription/Stores/SpeakerStore/SpeakerStore";
import { P2Results } from "models/P2Results";
import {
    Blurb,
    ChatBlurb,
} from "../AcxTranscription/Components/BlurbBox/Blurb";
import { convertChatToBlurbs } from "../AcxTranscription/utils";

type ModelTaggedBlurbsProps = {
    data: P2Results[];
    blurbClassName?: string;
    sxBlurb?: SxProps;
    variant: "eddy" | "adverse";
    speakerChannelMap?: SpeakerChannelMap;
    isPhrasesTranscript?: boolean;
    chat?: IChatRecord[];
    transcription?: ITranscriptionSegment[];
    onBlurbClick?: (blurb: Blurb | ChatBlurb) => void;
};

const P2Blurbs: React.FC<ModelTaggedBlurbsProps> = ({
    chat,
    data,
    blurbClassName,
    transcription,
    variant,
    speakerChannelMap,
    sxBlurb,
    isPhrasesTranscript,
    onBlurbClick,
}) => {
    const [isSingleBlurb, setIsSingleBlurb] = useState(false);
    const [blurbGroups, setBlurbGroups] = useState<(Blurb | ChatBlurb)[][]>([]);

    const setBlurbGroupsFromChat = useCallback(() => {
        if (!chat) return;

        const blurbs = convertChatToBlurbs(
            chat,
            variant === "eddy" ? data : undefined, // eddyData
            variant === "adverse" ? data : undefined, // adverseData
        ).filter((blurb) => blurb.foundAdverse || blurb.foundEddy);

        // Group chat blurbs by overlap with data ranges
        const groups = data.map((item) => {
            return blurbs.filter((blurb) => {
                if (
                    !(blurb.foundEddy || blurb.foundAdverse) ||
                    blurb.firstWordIndex === undefined ||
                    blurb.lastWordIndex === undefined
                ) {
                    return false;
                }
                // Overlap condition: blurb starts before or at range end, and ends after or at range start
                return (
                    blurb.firstWordIndex <= item.endWordIndex &&
                    blurb.lastWordIndex >= item.startWordIndex
                );
            });
        });

        const singleBlurb =
            uniqBy(transcription, (word) => word.channel).length === 1 &&
            blurbs.length === 1 &&
            data.length > 1;

        setIsSingleBlurb(singleBlurb);
        setBlurbGroups(groups);
    }, [chat, data, transcription, variant]);

    const setBlurbGroupsFromTranscript = useCallback(() => {
        if (!transcription || transcription.length === 0) return;

        const blurbs = convertTranscriptToBlurbs(
            transcription,
            variant === "eddy" ? data : undefined, // eddyData
            variant === "adverse" ? data : undefined, // adverseData
            isPhrasesTranscript,
        ).filter((blurb) => blurb.foundAdverse || blurb.foundEddy);

        const groups = data.map((item) => {
            // Include blurbs that overlap with the range [item.startWordIndex, item.endWordIndex]
            return blurbs.filter((blurb) => {
                if (
                    !(blurb.foundEddy || blurb.foundAdverse) ||
                    blurb.firstWordIndex === undefined ||
                    blurb.lastWordIndex === undefined
                ) {
                    return false;
                }
                // Overlap condition: blurb starts before or at range end, and ends after or at range start
                return (
                    blurb.firstWordIndex <= item.endWordIndex &&
                    blurb.lastWordIndex >= item.startWordIndex
                );
            });
        });

        const singleBlurb =
            uniqBy(transcription, (word) => word.channel).length === 1 &&
            blurbs.length === 1 &&
            data.length > 1;

        setIsSingleBlurb(singleBlurb);

        setBlurbGroups(groups);
    }, [transcription, data, variant, isPhrasesTranscript]);

    useEffect(() => {
        if (chat !== undefined && chat.length > 0) {
            setBlurbGroupsFromChat();
        } else if (transcription !== undefined && transcription.length > 0) {
            setBlurbGroupsFromTranscript();
        }
    }, [
        setBlurbGroupsFromChat,
        setBlurbGroupsFromTranscript,
        chat,
        transcription,
    ]);

    const theme = useTheme();

    return (
        <Box>
            {!isSingleBlurb ? (
                blurbGroups.map((group, groupIndex) => (
                    <React.Fragment
                        key={`${groupIndex}-${data[groupIndex]?.startWordIndex}-${data[groupIndex]?.endWordIndex}`}
                    >
                        {chat &&
                            (group as ChatBlurb[]).map((blurb, idx) => (
                                <P2BlurbBox
                                    key={`${idx} ${blurb.timestamp}`}
                                    blurb={blurb}
                                    className={blurbClassName}
                                    sx={sxBlurb}
                                    variant={variant}
                                    speakerChannelMap={speakerChannelMap}
                                    dataRange={data[groupIndex]}
                                    onClick={onBlurbClick}
                                />
                            ))}
                        {transcription &&
                            (group as Blurb[]).map((blurb, idx) => (
                                <P2BlurbBox
                                    key={`${idx} ${blurb.timeInterval.join(
                                        `-`,
                                    )} ${blurb.foundEddy ? "eddy" : ""} ${
                                        blurb.foundAdverse ? "adverse" : ""
                                    }`}
                                    blurb={blurb}
                                    className={blurbClassName}
                                    sx={sxBlurb}
                                    variant={variant}
                                    speakerChannelMap={speakerChannelMap}
                                    dataRange={data[groupIndex]}
                                    onClick={onBlurbClick}
                                />
                            ))}
                        {groupIndex !== blurbGroups.length - 1 && <Divider />}
                    </React.Fragment>
                ))
            ) : (
                <Typography
                    style={{
                        fontSize: "13px",
                        color: theme.palette.text.primary,
                        marginTop: "8px",
                    }}
                >
                    {`There was an issue displaying ${
                        variant === "eddy"
                            ? "Eddy Effect Signals"
                            : "Safety Event Signals"
                    } for this conversation.${
                        variant === "eddy"
                            ? " You can still view them on the Transcription tab, highlighted in blue."
                            : ""
                    }`}
                </Typography>
            )}
        </Box>
    );
};

export default P2Blurbs;
